export interface Label {
  edit?: Edit
  id?: string
  commonLabels?: CommonLabel[]
  fieldLabels?: FieldLabel[]
}

export interface CommonLabel {
  path?: string
  commonType?: string
}

export interface CommonLabelDefinition {
  id?: string
  name?: LangString
  description?: LangString
  labelType?: CommonLabelType
  indexType?: IndexDataType
  validFieldTypes?: IndexDataType[]
  allowList?: boolean
  codeKey?: string
}

export interface FieldLabel {
  id?: string
  permission?: Permission
  sortKey?: string
  databaseName?: LangString
  database?: string
  path?: string
  originalPath?: string
  storeType?: IndexStoreType
  indexType?: IndexDataType
  label?: LangString
  unique?: number
  filled?: number
  description?: LangString
  sample?: string
  codeKey?: string
}

export type CommonLabelType =
  'mandatory' |
  'mandatory_if_exists' |
  'arbitrary' |
  'auto'
export const COMMON_LABEL_TYPE_DEF: EnumDef = Object.freeze({
  id: 'jps-common_label_type',
  c: {
    mandatory: {
      l: {
        ja: '必須',
        en: 'Mandatory'
      },
      d: {
        ja: '必須項目。連携されるデータの項目のうちいずれか一つに、このラベルが付与されなければならない。この項目が無い場合、システムとして正常に機能しない項目。'
      }
    },
    mandatory_if_exists: {
      l: {
        ja: 'あれば必須',
        en: 'Mandatory if exists'
      },
      d: {
        ja: 'あれば必須項目。連携されるデータの項目に存在している場合、そのうちいずれか一つに、このラベルが付与されなければならない。この項目が無くてもシステムとしては機能するが、規約として付与を強制している項目。'
      }
    },
    arbitrary: {
      l: {
        ja: '任意',
        en: 'Arbitrary'
      },
      d: {
        ja: '任意項目。ラベルを付与することで、検索結果表示が強化されたり、ファセットでの絞込みが可能となったりする項目。'
      }
    },
    auto: {
      l: {
        ja: 'DB由来等、設定とは無関係にシステム側で追加されるフィールド',
        en: 'From DB'
      },
      d: {}
    }
  }
})
export type IndexDataType =
  'string' |
  'keyword' |
  'html' |
  'uri' |
  'date' |
  'latlon' |
  'code' |
  'number' |
  'exists' |
  'none'
export const INDEX_DATA_TYPE_DEF: EnumDef = Object.freeze({
  id: 'jps-index_data_type',
  c: {
    string: {
      l: {
        ja: '文字列',
        en: 'String'
      },
      d: {}
    },
    keyword: {
      l: {
        ja: '完全一致文字列',
        en: 'Exact Match String'
      },
      d: {}
    },
    html: {
      l: {
        ja: 'HTML',
        en: 'HTML'
      },
      d: {}
    },
    uri: {
      l: {
        ja: 'URI',
        en: 'URI'
      },
      d: {}
    },
    date: {
      l: {
        ja: '日付',
        en: 'Date'
      },
      d: {}
    },
    latlon: {
      l: {
        ja: '座標',
        en: 'Coordinates'
      },
      d: {}
    },
    code: {
      l: {
        ja: 'コード値',
        en: 'Code'
      },
      d: {}
    },
    number: {
      l: {
        ja: '数字',
        en: 'Numeric'
      },
      d: {}
    },
    exists: {
      l: {
        ja: 'データ有無',
        en: 'Exists'
      },
      d: {}
    },
    none: {
      l: {
        ja: '検索対象外',
        en: 'Non-Searchable'
      },
      d: {}
    }
  }
})
export type IndexStoreType =
  'normal' |
  'guionly' |
  'ignore'
export const INDEX_STORE_TYPE_DEF: EnumDef = Object.freeze({
  id: 'jps-index_store_type',
  c: {
    normal: {
      l: {
        ja: '通常',
        en: 'Store'
      },
      d: {}
    },
    guionly: {
      l: {
        ja: 'API不可',
        en: 'W/O API'
      },
      d: {}
    },
    ignore: {
      l: {
        ja: '非登録',
        en: 'Exclude'
      },
      d: {}
    }
  }
})
export const COMMON_LABEL_DEF: CommonLabelDefinition[] = [
  {
    id: 'id',
    name: {
      ja: 'ID',
      en: 'ID'
    },
    description: {
      ja: '',
      en: ''
    },
    labelType: 'mandatory',
    indexType: 'keyword',
    validFieldTypes: [
      'string',
      'number'
    ],
    allowList: false
  },
  {
    id: 'title',
    name: {
      ja: '名称/タイトル',
      en: 'Title'
    },
    description: {
      ja: '',
      en: ''
    },
    labelType: 'mandatory',
    indexType: 'string',
    validFieldTypes: [
      'string'
    ],
    allowList: false
  },
  {
    id: 'titleEn',
    name: {
      ja: '名称/タイトル英語',
      en: 'Title English'
    },
    description: {
      ja: '',
      en: ''
    },
    labelType: 'mandatory_if_exists',
    indexType: 'string',
    validFieldTypes: [
      'string'
    ],
    allowList: false
  },
  {
    id: 'titleYomi',
    name: {
      ja: '名称/タイトルヨミ',
      en: 'Title Pronounce'
    },
    description: {
      ja: '',
      en: ''
    },
    labelType: 'mandatory_if_exists',
    indexType: 'string',
    validFieldTypes: [
      'string'
    ],
    allowList: false
  },
  {
    id: 'lastUpdatedDate',
    name: {
      ja: '最終更新日',
      en: 'Last Updated'
    },
    description: {
      ja: '',
      en: ''
    },
    labelType: 'mandatory_if_exists',
    indexType: 'date',
    validFieldTypes: [
      'date'
    ],
    allowList: false
  },
  {
    id: 'linkUrl',
    name: {
      ja: 'URL',
      en: 'URL'
    },
    description: {
      ja: '',
      en: ''
    },
    labelType: 'mandatory_if_exists',
    indexType: 'none',
    validFieldTypes: [
      'uri'
    ],
    allowList: false
  },
  {
    id: 'thumbnailUrl',
    name: {
      ja: 'サムネイル画像URL',
      en: 'Thumbnail URL'
    },
    description: {
      ja: '',
      en: ''
    },
    labelType: 'mandatory_if_exists',
    indexType: 'keyword',
    validFieldTypes: [
      'uri'
    ],
    allowList: true
  },
  {
    id: 'contentsUrl',
    name: {
      ja: 'コンテンツURL',
      en: 'Contents URL'
    },
    description: {
      ja: '',
      en: ''
    },
    labelType: 'arbitrary',
    indexType: 'keyword',
    validFieldTypes: [
      'uri'
    ],
    allowList: true
  },
  {
    id: 'iiifUrl',
    name: {
      ja: 'IIIFマニフェストURL',
      en: 'IIIF Manifest URL'
    },
    description: {
      ja: '',
      en: ''
    },
    labelType: 'mandatory_if_exists',
    indexType: 'keyword',
    validFieldTypes: [
      'uri'
    ],
    allowList: false
  },
  {
    id: 'contentsType',
    name: {
      ja: 'コンテンツフォーマット',
      en: 'Contents Format'
    },
    description: {
      ja: 'この共通項目ラベルを設定した場合、個別のメタデータに正しい値が設定されていれば、データベースの値を上書きします。',
      en: ''
    },
    labelType: 'arbitrary',
    indexType: 'code',
    validFieldTypes: [
      'code'
    ],
    allowList: false,
    codeKey: 'jps-digital_contents_type'
  },
  {
    id: 'contentsRightsType',
    name: {
      ja: 'コンテンツの権利区分',
      en: 'Contents Rights Type'
    },
    description: {
      ja: 'この共通項目ラベルを設定した場合、個別のメタデータに正しい値が設定されていれば、データベースの値を上書きします。',
      en: ''
    },
    labelType: 'mandatory_if_exists',
    indexType: 'code',
    validFieldTypes: [
      'code'
    ],
    allowList: false,
    codeKey: 'jps-rights_type'
  },
  {
    id: 'contentsAccess',
    name: {
      ja: 'コンテンツ公開状況',
      en: 'Contents Access'
    },
    description: {
      ja: 'この共通項目ラベルを設定した場合、個別のメタデータに正しい値が設定されていれば、データベースの値を上書きします。',
      en: ''
    },
    labelType: 'mandatory_if_exists',
    indexType: 'code',
    validFieldTypes: [
      'code'
    ],
    allowList: false,
    codeKey: 'jps-contents_access'
  },
  {
    id: 'category',
    name: {
      ja: 'カテゴリー',
      en: 'Category'
    },
    description: {
      ja: 'この共通項目ラベルを設定した場合、個別のメタデータに正しい値が設定されていれば、データベースの値を上書きします。',
      en: ''
    },
    labelType: 'arbitrary',
    indexType: 'code',
    validFieldTypes: [
      'code'
    ],
    allowList: true,
    codeKey: 'jps-db_category'
  },
  {
    id: 'description',
    name: {
      ja: '解説',
      en: 'Description'
    },
    description: {
      ja: '',
      en: ''
    },
    labelType: 'arbitrary',
    indexType: 'string',
    validFieldTypes: [
      'string',
      'html'
    ],
    allowList: false
  },
  {
    id: 'descriptionEn',
    name: {
      ja: '解説（英語）',
      en: 'DescriptionEn'
    },
    description: {
      ja: '',
      en: ''
    },
    labelType: 'arbitrary',
    indexType: 'string',
    validFieldTypes: [
      'string',
      'html'
    ],
    allowList: false
  },
  {
    id: 'contributor',
    name: {
      ja: '人物/団体',
      en: 'Contributor'
    },
    description: {
      ja: '',
      en: ''
    },
    labelType: 'arbitrary',
    indexType: 'string',
    validFieldTypes: [
      'string'
    ],
    allowList: true
  },
  {
    id: 'temporal',
    name: {
      ja: '時間/時代',
      en: 'Time'
    },
    description: {
      ja: '',
      en: ''
    },
    labelType: 'arbitrary',
    indexType: 'string',
    validFieldTypes: [
      'string',
      'date'
    ],
    allowList: true
  },
  {
    id: 'location',
    name: {
      ja: '場所',
      en: 'Location'
    },
    description: {
      ja: '',
      en: ''
    },
    labelType: 'arbitrary',
    indexType: 'string',
    validFieldTypes: [
      'string'
    ],
    allowList: true
  },
  {
    id: 'coordinates',
    name: {
      ja: '座標',
      en: 'Coordinates'
    },
    description: {
      ja: '',
      en: ''
    },
    labelType: 'arbitrary',
    indexType: 'latlon',
    validFieldTypes: [
      'latlon'
    ],
    allowList: false
  },
  {
    id: 'provider',
    name: {
      ja: '所蔵機関',
      en: 'Provider'
    },
    description: {
      ja: '',
      en: ''
    },
    labelType: 'mandatory_if_exists',
    indexType: 'keyword',
    validFieldTypes: [
      'string',
      'keyword'
    ],
    allowList: false
  },
  {
    id: 'providerUrl',
    name: {
      ja: '所蔵機関URL',
      en: 'Provider URL'
    },
    description: {
      ja: '',
      en: ''
    },
    labelType: 'arbitrary',
    indexType: 'none',
    validFieldTypes: [
      'uri'
    ],
    allowList: false
  },
  {
    id: 'ownerOrg',
    name: {
      ja: 'つなぎ役機関',
      en: 'Provided by'
    },
    description: {
      ja: '',
      en: ''
    },
    labelType: 'auto',
    indexType: 'keyword',
    validFieldTypes: [],
    allowList: false
  },
  {
    id: 'database',
    name: {
      ja: 'データベース',
      en: 'Database'
    },
    description: {
      ja: '',
      en: ''
    },
    labelType: 'auto',
    indexType: 'keyword',
    validFieldTypes: [],
    allowList: false
  },
  {
    id: 'apiType',
    name: {
      ja: 'API対応',
      en: 'API対応'
    },
    description: {
      ja: '',
      en: ''
    },
    labelType: 'auto',
    indexType: 'code',
    validFieldTypes: [],
    allowList: false,
    codeKey: 'jps-digital_contents_type'
  },
  {
    id: 'subCategory',
    name: {
      ja: 'サブカテゴリー',
      en: 'Sub Category'
    },
    description: {
      ja: '',
      en: ''
    },
    labelType: 'auto',
    indexType: 'keyword',
    validFieldTypes: [],
    allowList: true
  },
  {
    id: 'access',
    name: {
      ja: '公開範囲',
      en: 'Publication'
    },
    description: {
      ja: '',
      en: ''
    },
    labelType: 'auto',
    indexType: 'code',
    validFieldTypes: [],
    allowList: false
  },
  {
    id: 'dclass',
    name: {
      ja: '分類（自動付与）',
      en: 'Class（Auto）'
    },
    description: {
      ja: '',
      en: ''
    },
    labelType: 'auto',
    indexType: 'keyword',
    validFieldTypes: [],
    allowList: false
  },
  {
    id: 'subject',
    name: {
      ja: '主題',
      en: 'Subject'
    },
    description: {
      ja: '',
      en: ''
    },
    labelType: 'arbitrary',
    indexType: 'string',
    validFieldTypes: [
      'string'
    ],
    allowList: true
  },
  {
    id: 'language',
    name: {
      ja: '言語',
      en: 'Language'
    },
    description: {
      ja: '',
      en: ''
    },
    labelType: 'arbitrary',
    indexType: 'code',
    validFieldTypes: [
      'code'
    ],
    allowList: true,
    codeKey: 'jps-language'
  }
]
Object.freeze(COMMON_LABEL_DEF)
